import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePlayer } from "@/context/PlayerContext";
import TrackRow from "@/components/TrackRow";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ListMusic, Plus, Play, Trash2, Lock, Crown, ArrowLeft } from "lucide-react";

function PremiumGate() {
  const { isGuest } = useAuth();
  const nav = useNavigate();
  return (
    <div className="px-6 md:px-12 py-16 max-w-xl">
      <Lock className="text-primary mb-4" size={40} />
      <div className="overline text-xs text-accent mb-2">Members only</div>
      <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-[0.95] mb-4">
        BUILD YOUR<br /><span className="text-primary">PLAYLISTS.</span>
      </h1>
      <p className="text-muted-foreground mb-8">
        Playlists are a premium feature. Go premium for $5/month to build unlimited playlists and stream them full-length — no cutoffs.
      </p>
      <Button data-testid="playlists-gate-cta" onClick={() => nav(isGuest ? "/register" : "/subscribe")} className="bg-primary hover:bg-white hover:text-primary rounded-none font-bold overline h-12 px-8">
        <Crown size={16} className="mr-2" /> Unlock for $5/mo
      </Button>
    </div>
  );
}

export default function Playlists() {
  const { isPremium } = useAuth();
  const { playTrack } = usePlayer();
  const [playlists, setPlaylists] = useState([]);
  const [selected, setSelected] = useState(null);
  const [allTracks, setAllTracks] = useState([]);
  const [name, setName] = useState("");

  const loadLists = () => api.get("/playlists").then((r) => setPlaylists(r.data)).catch(() => {});
  const openList = (id) => api.get(`/playlists/${id}`).then((r) => setSelected(r.data)).catch(() => {});

  useEffect(() => {
    if (isPremium) {
      loadLists();
      api.get("/tracks").then((r) => setAllTracks(r.data)).catch(() => {});
    }
  }, [isPremium]);

  if (!isPremium) return <PremiumGate />;

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await api.post("/playlists", { name });
      setName("");
      toast.success("Playlist created");
      loadLists();
    } catch { toast.error("Could not create playlist"); }
  };

  const removeList = async (id) => {
    if (!window.confirm("Delete this playlist?")) return;
    await api.delete(`/playlists/${id}`);
    toast.success("Playlist deleted");
    if (selected?.id === id) setSelected(null);
    loadLists();
  };

  const addTrack = async (tid) => {
    await api.post(`/playlists/${selected.id}/tracks`, { track_id: tid });
    openList(selected.id);
    loadLists();
  };

  const removeTrack = async (track) => {
    await api.delete(`/playlists/${selected.id}/tracks/${track.id}`);
    openList(selected.id);
    loadLists();
    toast.success("Removed from playlist");
  };

  // DETAIL VIEW
  if (selected) {
    const inList = new Set((selected.tracks || []).map((t) => t.id));
    const addable = allTracks.filter((t) => !inList.has(t.id));
    return (
      <div className="px-6 md:px-12 py-10">
        <button data-testid="playlist-back" onClick={() => setSelected(null)} className="flex items-center gap-2 text-muted-foreground hover:text-white text-xs overline mb-6">
          <ArrowLeft size={16} /> All Playlists
        </button>
        <div className="flex items-end justify-between gap-4 mb-8">
          <div>
            <div className="overline text-xs text-accent mb-2">Playlist</div>
            <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">{selected.name}</h1>
            <div className="text-sm text-muted-foreground mt-2">{selected.tracks?.length || 0} tracks</div>
          </div>
          {selected.tracks?.length > 0 && (
            <Button data-testid="playlist-play-all" onClick={() => playTrack(selected.tracks[0], selected.tracks)} className="bg-primary hover:bg-white hover:text-primary rounded-none font-bold overline h-12 px-6">
              <Play size={18} className="mr-2" /> Play
            </Button>
          )}
        </div>

        {selected.tracks?.length > 0 ? (
          <div className="border-t border-border mb-12">
            {selected.tracks.map((t, i) => <TrackRow key={t.id} track={t} index={i} list={selected.tracks} onDelete={removeTrack} />)}
          </div>
        ) : (
          <div className="border-2 border-dashed border-border p-10 text-center text-muted-foreground text-sm mb-12">Empty playlist. Add tracks below.</div>
        )}

        <h2 className="font-display text-lg font-bold overline tracking-widest mb-4">Add Tracks</h2>
        {addable.length === 0 ? (
          <div className="text-sm text-muted-foreground">No more tracks to add.</div>
        ) : (
          <div className="border-t border-border">
            {addable.map((t) => (
              <div key={t.id} data-testid={`add-track-${t.id}`} className="flex items-center justify-between gap-4 px-4 py-3 border-b border-border">
                <div className="min-w-0"><div className="font-display font-medium truncate">{t.title}</div><div className="text-xs text-muted-foreground truncate">{t.artist}</div></div>
                <button data-testid={`add-track-btn-${t.id}`} onClick={() => addTrack(t.id)} className="shrink-0 flex items-center gap-1 bg-accent text-black px-3 py-1.5 text-xs overline font-bold hover:bg-white transition-colors">
                  <Plus size={14} /> Add
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // LIST VIEW
  return (
    <div className="px-6 md:px-12 py-10">
      <div className="overline text-xs text-accent mb-2 flex items-center gap-2"><Crown size={14} /> Premium</div>
      <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mb-6">YOUR PLAYLISTS</h1>

      <form onSubmit={create} className="flex gap-3 mb-10 max-w-lg">
        <Input data-testid="playlist-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="New playlist name..." className="h-12 bg-transparent border-2 border-border focus:border-primary rounded-none text-white" />
        <Button data-testid="playlist-create-btn" type="submit" className="bg-primary hover:bg-white hover:text-primary rounded-none font-bold overline h-12 px-6 shrink-0"><Plus size={16} className="mr-1" /> Create</Button>
      </form>

      {playlists.length === 0 ? (
        <div className="border-2 border-dashed border-border p-12 text-center text-muted-foreground text-sm">No playlists yet. Create one above.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {playlists.map((p) => (
            <div key={p.id} data-testid={`playlist-card-${p.id}`} className="group bg-card border border-border hover:border-primary transition-colors p-5 cursor-pointer" onClick={() => openList(p.id)}>
              <div className="flex items-start justify-between">
                <ListMusic className="text-primary mb-4" size={28} />
                <button data-testid={`playlist-delete-${p.id}`} onClick={(e) => { e.stopPropagation(); removeList(p.id); }} className="text-muted-foreground hover:text-primary opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={16} /></button>
              </div>
              <div className="font-display text-xl font-bold truncate">{p.name}</div>
              <div className="text-xs text-muted-foreground mt-1">{p.track_count} tracks</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
